import styled from "@emotion/styled";
import Fade from "@mui/material/Fade";
import Menu, { type MenuProps } from "@mui/material/Menu";
import { cloneElement, useMemo, useState, type ComponentProps, type ReactElement } from "react";

import { IconButton } from "../buttons/Buttons";
import { Icon } from "../icons/Icon";

const StyledMenu = styled(Menu)`
  .fa-icon-wrapper {
    color: ${({ theme }) => theme.generalTextIcon};
  }
`;

export const MenuItemLabel = styled.span`
  margin-left: 5px;
`;

export const ContextMenuIconButton = styled(IconButton)`
  font-size: 16px;
  color: inherit;
`;

type Props = Omit<MenuProps, "open"> & {
  // Menu contents, normally `MenuItem`s.
  children?: React.ReactNode;
  tabIndex?: number;
  closeOnSelection?: boolean;
  IconComponent?: React.ReactNode;
  disabled?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  ["data-test"]: string;
};

// Similar to ./PopMenu.tsx but uses material-ui.
export const ContextMenu = ({
  children,
  disabled,
  tabIndex,
  closeOnSelection = true,
  IconComponent,
  onMenuOpen,
  onMenuClose,
  className,
  ...rest
}: Props) => {
  const dataTest = (rest as Record<string, unknown>)["data-test"] as string;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItemsClone, setMenuItemsClone] = useState<ReactElement<any> | undefined>(undefined);

  const menuItems = <div data-test="list">{children}</div>;

  const open = useMemo(() => {
    if (disabled) return false;
    return Boolean(anchorEl);
  }, [anchorEl, disabled]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuItemsClone(undefined);
    onMenuOpen?.();
  };

  const handleClose: ComponentProps<typeof Menu>["onClose"] = (event: MouseEvent, reason) => {
    if (reason === "backdropClick") event.stopPropagation();
    setAnchorEl(null);
    onMenuClose?.();
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (closeOnSelection) {
      // note: keeps menu items stable while fade out transition completes
      setMenuItemsClone(cloneElement(menuItems));
      setAnchorEl(null);
    }
  };

  return (
    <>
      <ContextMenuIconButton
        aria-controls="fade-menu"
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
        tabIndex={tabIndex}
        disabled={disabled}
        className={className}
        data-test={dataTest}
      >
        {IconComponent ?? <Icon icon="ellipsis-v" />}
      </ContextMenuIconButton>
      <StyledMenu
        id="fade-menu"
        open={open}
        anchorEl={anchorEl}
        onClick={handleMenuClick}
        onClose={handleClose}
        slots={{
          ...rest.slots,
          transition: Fade,
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        {...rest}
        data-test="context-menu-container"
      >
        {menuItemsClone ?? menuItems}
      </StyledMenu>
    </>
  );
};

export default ContextMenu;
